import React, { useState, useEffect, createContext, useContext } from 'react';
import DiscreteBanner from './DiscreteBanner';
import NativeAdCard from './NativeAdCard';
import InterstitialAd from './InterstitialAd';
import RewardedAd from './RewardedAd';

// Context pour la gestion globale des publicités
const AdContext = createContext();

export const useAds = () => {
  const context = useContext(AdContext);
  if (!context) {
    throw new Error('useAds must be used within an AdProvider');
  }
  return context;
};

// Configuration des publicités par page/section
const AD_PLACEMENT_CONFIG = {
  // Pages où les bannières sont autorisées
  bannerPages: ['home', 'discover', 'chat', 'groups'],
  
  // Pages où les publicités natives sont autorisées
  nativePages: ['home', 'discover', 'groups', 'timeline'],
  
  // Actions qui déclenchent des interstitielles
  interstitialTriggers: [
    'group-created',
    'payment-completed', 
    'member-joined',
    'profile-updated',
    'transition'
  ],
  
  // Pages interdites pour toute publicité (zones sensibles)
  restrictedPages: [
    'payment',
    'transaction',
    'member-management',
    'admin-panel',
    'security-settings',
    'password-change'
  ],
  
  // Fréquence d'affichage (en secondes)
  displayFrequency: {
    banner: 180, // 3 minutes
    native: 300, // 5 minutes
    interstitial: 600, // 10 minutes
    rewarded: 0 // Pas de limite (initié par l'utilisateur)
  }
};

// Provider principal pour la gestion des publicités
export const AdProvider = ({ children, user, currentPage = 'home' }) => {
  const [adState, setAdState] = useState({
    banners: {
      isVisible: false,
      lastShown: 0,
      currentAd: null
    },
    native: {
      shouldShow: false,
      lastShown: 0,
      frequency: 3 // Toutes les 3 publications
    },
    interstitial: {
      isVisible: false,
      lastShown: 0,
      context: null
    },
    rewarded: {
      isVisible: false,
      rewardType: null,
      context: null
    }
  });

  const [userPreferences, setUserPreferences] = useState({
    allowPersonalizedAds: true,
    allowRewardedAds: true,
    adBlockingEnabled: false,
    maxAdsPerSession: 5
  });

  const [sessionData, setSessionData] = useState({
    adsShown: 0,
    rewardsEarned: 0,
    startTime: Date.now(),
    userActions: []
  });

  // Vérifier si les publicités sont autorisées sur la page actuelle
  const isPageRestricted = (page) => {
    return AD_PLACEMENT_CONFIG.restrictedPages.includes(page);
  };

  // Vérifier la fréquence d'affichage
  const canShowAdType = (adType) => {
    const now = Date.now();
    const lastShown = adState[adType]?.lastShown || 0;
    const frequency = AD_PLACEMENT_CONFIG.displayFrequency[adType] * 1000; // Convert to ms
    
    return (now - lastShown) >= frequency;
  };

  // Gérer l'affichage des bannières
  const showBanner = (page = currentPage) => {
    if (isPageRestricted(page) || 
        !AD_PLACEMENT_CONFIG.bannerPages.includes(page) ||
        !canShowAdType('banner') ||
        sessionData.adsShown >= userPreferences.maxAdsPerSession) {
      return;
    }

    setAdState(prev => ({
      ...prev,
      banners: {
        ...prev.banners,
        isVisible: true,
        lastShown: Date.now()
      }
    }));

    trackAdEvent('banner_shown', { page });
  };

  // Gérer les publicités natives
  const shouldShowNativeAd = (postIndex) => {
    if (isPageRestricted(currentPage) ||
        !AD_PLACEMENT_CONFIG.nativePages.includes(currentPage) ||
        !canShowAdType('native')) {
      return false;
    }

    return postIndex % adState.native.frequency === 0 && postIndex > 0;
  };

  // Gérer les interstitielles
  const showInterstitial = (context = 'transition') => {
    if (isPageRestricted(currentPage) ||
        !AD_PLACEMENT_CONFIG.interstitialTriggers.includes(context) ||
        !canShowAdType('interstitial') ||
        sessionData.adsShown >= userPreferences.maxAdsPerSession) {
      return;
    }

    setAdState(prev => ({
      ...prev,
      interstitial: {
        isVisible: true,
        lastShown: Date.now(),
        context: context
      }
    }));

    trackAdEvent('interstitial_shown', { context });
  };

  // Gérer les publicités rewarded
  const showRewardedAd = (rewardType = 'premium-features', context = 'user-initiated') => {
    if (!userPreferences.allowRewardedAds) {
      return;
    }

    setAdState(prev => ({
      ...prev,
      rewarded: {
        isVisible: true,
        rewardType: rewardType,
        context: context
      }
    }));

    trackAdEvent('rewarded_shown', { rewardType, context });
  };

  // Fermer les publicités
  const closeBanner = () => {
    setAdState(prev => ({
      ...prev,
      banners: {
        ...prev.banners,
        isVisible: false
      }
    }));
  };

  const closeInterstitial = () => {
    setAdState(prev => ({
      ...prev,
      interstitial: {
        ...prev.interstitial,
        isVisible: false,
        context: null
      }
    }));
  };

  const closeRewarded = () => {
    setAdState(prev => ({
      ...prev,
      rewarded: {
        isVisible: false,
        rewardType: null,
        context: null
      }
    }));
  };

  // Gérer les clics sur les publicités
  const handleAdClick = (adData, source = 'unknown') => {
    trackAdEvent('ad_clicked', { 
      adId: adData.id, 
      source: source,
      adType: adData.type
    });

    // Analytics ou redirection externe
    console.log('Ad clicked:', adData);
    
    // Simuler l'ouverture d'un lien externe
    if (adData.externalUrl) {
      window.open(adData.externalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Gérer les récompenses gagnées
  const handleRewardEarned = (rewardData) => {
    setSessionData(prev => ({
      ...prev,
      rewardsEarned: prev.rewardsEarned + 1
    }));

    trackAdEvent('reward_earned', {
      rewardType: rewardData.type,
      rewardValue: rewardData.value
    });

    // Appliquer la récompense à l'utilisateur
    applyReward(rewardData);
  };

  // Appliquer les récompenses
  const applyReward = (rewardData) => {
    switch (rewardData.type) {
      case 'premium-trial':
        // Activer l'essai premium
        console.log('Activating premium trial:', rewardData);
        break;
      case 'customization-pack':
        // Débloquer les options de personnalisation
        console.log('Unlocking customization:', rewardData);
        break;
      case 'achievement-badge':
        // Ajouter le badge au profil
        console.log('Adding badge:', rewardData);
        break;
      case 'virtual-coins':
        // Ajouter les pièces au compte
        console.log('Adding coins:', rewardData);
        break;
      default:
        console.log('Unknown reward type:', rewardData);
    }
  };

  // Tracking des événements publicitaires
  const trackAdEvent = (event, data = {}) => {
    setSessionData(prev => ({
      ...prev,
      userActions: [...prev.userActions, {
        timestamp: Date.now(),
        event: event,
        data: data
      }],
      adsShown: event.includes('_shown') ? prev.adsShown + 1 : prev.adsShown
    }));

    // Ici vous pouvez intégrer avec vos services d'analytics
    console.log('Ad Event:', event, data);
  };

  // Auto-affichage des bannières selon la page
  useEffect(() => {
    if (!isPageRestricted(currentPage) && canShowAdType('banner')) {
      const timer = setTimeout(() => {
        showBanner(currentPage);
      }, 3000); // Attendre 3 secondes après le chargement de la page

      return () => clearTimeout(timer);
    }
  }, [currentPage]);

  // Valeurs du contexte
  const contextValue = {
    // État
    adState,
    userPreferences,
    sessionData,
    currentPage,
    
    // Actions
    showBanner,
    showInterstitial,
    showRewardedAd,
    closeBanner,
    closeInterstitial,
    closeRewarded,
    shouldShowNativeAd,
    
    // Handlers
    handleAdClick,
    handleRewardEarned,
    
    // Utilitaires
    isPageRestricted,
    canShowAdType,
    
    // Configuration
    setUserPreferences
  };

  return (
    <AdContext.Provider value={contextValue}>
      {children}
      
      {/* Bannières discrètes */}
      {adState.banners.isVisible && !isPageRestricted(currentPage) && (
        <DiscreteBanner
          position="bottom"
          page={currentPage}
          onClose={closeBanner}
          onAdClick={handleAdClick}
          isVisible={true}
        />
      )}

      {/* Publicités interstitielles */}
      {adState.interstitial.isVisible && (
        <InterstitialAd
          isVisible={true}
          onClose={closeInterstitial}
          onAdClick={handleAdClick}
          context={adState.interstitial.context}
        />
      )}

      {/* Publicités rewarded */}
      {adState.rewarded.isVisible && (
        <RewardedAd
          isVisible={true}
          onClose={closeRewarded}
          onRewardEarned={handleRewardEarned}
          onAdCompleted={(adData) => trackAdEvent('rewarded_completed', { adId: adData.id })}
          rewardType={adState.rewarded.rewardType}
          trigger={adState.rewarded.context}
        />
      )}
    </AdContext.Provider>
  );
};

// Hook pour les composants qui veulent afficher des publicités natives
export const useNativeAd = (postIndex = 0) => {
  const { shouldShowNativeAd, handleAdClick, currentPage } = useAds();
  
  const showNative = shouldShowNativeAd(postIndex);
  
  return {
    shouldShowNative: showNative,
    NativeAdComponent: showNative ? (
      <NativeAdCard
        adType="feed"
        placement={currentPage}
        onAdClick={handleAdClick}
        onAdInteraction={(adId, action, value) => {
          console.log('Native ad interaction:', { adId, action, value });
        }}
      />
    ) : null
  };
};

// Composant utilitaire pour déclencher des publicités rewarded
export const RewardedAdTrigger = ({ 
  children, 
  rewardType = 'premium-features',
  label = 'Regarder une pub pour gagner des récompenses'
}) => {
  const { showRewardedAd } = useAds();

  return (
    <button
      onClick={() => showRewardedAd(rewardType, 'user-initiated')}
      className="w-full flex items-center justify-center space-x-2 p-4 bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-lg hover:from-purple-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-105"
    >
      {children || (
        <>
          <span>🎁</span>
          <span>{label}</span>
        </>
      )}
    </button>
  );
};

export default AdProvider;